import React, { useState } from "react";
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Field, reduxForm } from 'redux-form';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { fetchLinkDocTypes } from '../../document/documentActions';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Checkbox from '@material-ui/core/Checkbox';
import renderDatePicker from '../../_shared/utils/renderDatePicker';
import renderSelectField from '../../_shared/utils/renderSelectField';
import renderTextField from '../../_shared/utils/renderTextField';
import { fetchExistingDocumentsToLink, updateDocumentLinkStatus } from '../../../services/document';
import _config from '../../../config';
import SSO from '../../../sso';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    margin: theme.spacing(7),
    minWidth: '54vw',
    maxWidth: '54vw',
    padding: theme.spacing(1),
  },
  dialogContent: {
    overflowY: 'unset',
  },
  resultsDialogPaper: {
    margin: theme.spacing(7),
    minWidth: '85vw',
    maxWidth: '85vw',
    padding: theme.spacing(1),
    maxHeight: '80vh',
  },
  resultsDialogContent: {
    overflowY: 'auto',
  },
  tableHeader: {
    backgroundColor: '#1a2a3a',
    '& th': {
      color: '#ffffff',
      fontWeight: 'bold',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#f5f5f5',
    },
  },
  clickableCell: {
    cursor: 'pointer',
    color: '#1976d2',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sortableHeader: {
    cursor: 'pointer',
    '& .MuiTableSortLabel-root': {
      color: '#ffffff',
      '&:hover': {
        color: '#ffffff',
      },
      '&.MuiTableSortLabel-active': {
        color: '#ffffff',
      },
    },
    '& .MuiTableSortLabel-icon': {
      color: '#ffffff !important',
    },
  },
}));

const LinkDocuments = (props) => {
  const {
    open,
    handleClose,
    linkDocumentTypes,
    onFetchLinkDocTypes,
    onLinkCompleted,
    projectId,
    categoryId,
    companyNumber,
    existingDocuments,
    reset,
  } = props;
  const classes = useStyles();
  // Phase enum: 'search' (search form) | 'results' (results table)
  // Redux 'open' prop controls modal family visibility; local phase controls which dialog
  const [phase, setPhase] = useState('search');
  const [selectedItems, setSelectedItems] = useState([]);
  const [linkDocumentsData, setLinkDocumentsData] = useState([]);
  const [page, setPage] = useState(0);
  const [linkError, setLinkError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const rowsPerPage = 10;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle date sorting for executedDate
      if (orderBy === 'executedDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else {
        // String comparison for fileName
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const formatDateParam = (dateValue, isEndDate = false) => {
    if (!dateValue) {
      return '';
    }

    const timeString = isEndDate ? 'T23:59:59.000Z' : 'T00:00:00.000Z';

    // Handle moment objects (from date picker)
    if (dateValue._isAMomentObject || (dateValue.toDate && typeof dateValue.toDate === 'function')) {
      const date = dateValue.toDate();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}${timeString}`;
    }

    // Handle native Date objects
    if (Object.prototype.toString.call(dateValue) === '[object Date]' && !Number.isNaN(dateValue.getTime())) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}${timeString}`;
    }

    // If it's already a string, try to parse and normalize it
    if (typeof dateValue === 'string' && dateValue.length > 0) {
      const parsed = new Date(dateValue);
      if (!Number.isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}${timeString}`;
      }
    }

    return dateValue;
  };

  const mapLinkDocumentsRows = (entries = []) =>
    entries.map((entry, index) => {
      const entryProps = entry?.content?.properties || {};
      return {
        id: entryProps.file_id || entryProps.r_object_id || `${index}`,
        fileId: entryProps.file_id || entryProps.r_object_id || '',
        storeNumber: entryProps.rel_store_number || '',
        fileName: entryProps.file_name || '',
        documentType: entryProps.rel_type || '',
        executedDate: entryProps.rel_doc_date || '',
        status: entryProps.rel_public_view == 1 ? 'Public Viewable' : (entryProps.rel_public_view || ''),
        facilityID: entryProps.rel_company_no || '',
        address: entryProps.rel_street_address || '',
        documentLinksCounts: entryProps.documentLinksCounts || 0,
      };
    });
  const handleSearch = async (values) => {
    if (!values.store || !values.doc_type) {
      return;
    }

    setIsSearching(true);

    const params = {
      storeNumber: String(values.store).trim(),
      companyNumber: String(companyNumber || '').trim(),
      relType: values.doc_type ? (values.doc_type.value || values.doc_type) : '',
      fileName: values.filename || '',
      fromDate: formatDateParam(values.date_range_from),
      toDate: formatDateParam(values.date_range_to, true),
    };

    try {
      const response = await fetchExistingDocumentsToLink(params);
      const graphData = response.data?.fetchExistingDocumentsToLink;
      const payload = graphData?.payload;
      let parsedPayload = payload;

      if (typeof payload === 'string') {
        try {
          parsedPayload = JSON.parse(payload);
        } catch (e) {
          console.warn('[Link Documents] Unable to parse payload string:', e);
        }
      }

      const rawData = parsedPayload?.entries || [];

      const mappedRows = mapLinkDocumentsRows(rawData);

      // Filter out documents that already exist in this project (uploaded or linked)
      // TODO: Ideally move this filtering server-side by adding projectId to the GraphQL query
      const existingDocIds = new Set(
        (existingDocuments || [])
          .map(doc => String(doc.documentum_doc_id || '').trim())
          .filter(Boolean)
      );

      const filteredRows = mappedRows.filter(row => {
        const rowFileId = String(row.fileId || '').trim();
        return !existingDocIds.has(rowFileId);
      });

      setLinkDocumentsData(filteredRows);
      setPage(0);
      setLinkError('');
      setPhase('results');
    } catch (error) {
      console.error('[Link Documents] fetchExistingDocumentsToLink error:', error);
      setLinkDocumentsData([]);
      setPage(0);
      setLinkError('');
      setPhase('results');
    } finally {
      setIsSearching(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleCheckboxChange = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleResultsClose = () => {
    setPhase('search');
    setSelectedItems([]);
    setLinkDocumentsData([]);
    setPage(0);
    setLinkError('');
  };

  const handleLink = async () => {
    const selectedRows = linkDocumentsData.filter(row => selectedItems.includes(row.id));

    const documents = selectedRows
      .map(row => ({
        fileId: String(row.fileId || row.id || '').trim(),
        fileName: String(row.fileName || '').trim(),
      }))
      .filter(item => item.fileId && item.fileName);

    if (!documents.length) {
      setLinkError('Selected documents are missing required file details.');
      return;
    }

    try {
      const response = await updateDocumentLinkStatus({
        projectId: Number.parseInt(projectId),
        categoryId: Number.parseInt(categoryId),
        documents,
        linkStatus: true,
      });

      const linkResult = response?.data?.updateDocumentLinkStatus;
      if (!linkResult?.Success) {
        // Log technical error for debugging, but show user-friendly message
        // to avoid exposing database internals
        const rawMessage = linkResult?.Message || '';
        console.error('[Link Documents] Link failed:', rawMessage);
        
        const isTechnicalError = /permission|denied|database|schema|INSERT|UPDATE|DELETE|SELECT/i.test(rawMessage);
        const userMessage = isTechnicalError
          ? 'Failed to link documents due to a system error. Please contact support.'
          : (rawMessage || 'Failed to link selected documents.');
        setLinkError(userMessage);
        return;
      }

      setLinkError('');
      onLinkCompleted();
    } catch (error) {
      console.error('[Link Documents] updateDocumentLinkStatus error:', error);
      setLinkError('Unable to link documents. Please try again.');
      return;
    }

    handleResultsClose();
    reset();
    handleClose();
  };

  const handleOpenDocument = (fileId, fileName) => {
    if (!fileId) {
      setLinkError('Cannot open document: File ID is missing.');
      return;
    }

    const fileIdPattern = /^[a-zA-Z0-9_-]+$/;
    if (!fileIdPattern.test(fileId)) {
      setLinkError('Cannot open document: Invalid file ID format.');
      return;
    }

    setLinkError('');

    try {
      const documentUrl = `${_config.BASE_URL}document?docID=${encodeURIComponent(fileId)}&isLatestVersion=true`;

      fetch(documentUrl, { credentials: 'include' })
        .then(async (response) => {
          if (response.status === 200) {
            return { success: true, blob: await response.blob() };
          }
          
          if (response.status === 401) {
            SSO.redirectToSignInUrl();
            return { success: false };
          }
          
          // Handle error responses (415, 404, 500, etc.)
          let errorMessage = "Unable to open document";
          
          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.toLowerCase().includes("application/json")) {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
              
              if (response.status === 415) {
                errorMessage = `Document type not supported: ${errorData.message || 'Expected PDF format'}`;
              } else if (response.status === 404) {
                errorMessage = 'Document not found. It may have been deleted or moved.';
              } else if (response.status >= 500) {
                errorMessage = 'Server error while retrieving document. Please try again later.';
              }
            } else {
              errorMessage = `Unable to open document (Status: ${response.status})`;
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorMessage = `Unable to open document (Status: ${response.status})`;
          }
          
          return { success: false, error: errorMessage };
        })
        .then(result => {
          if (!result || !result.success) {
            if (result && result.error) {
              setLinkError(`Failed to open "${fileName}": ${result.error}`);
            }
            return;
          }
          
          if (result.blob) {
            setLinkError('');
            const fileURL = URL.createObjectURL(result.blob);
            window.open(fileURL);
            setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
          }
        })
        .catch(err => {
          console.error('Error opening document:', err);
          setLinkError(`Failed to open "${fileName}": ${err.message}`);
        });
    } catch (error) {
      console.error('Error opening document:', error);
      setLinkError(`Failed to open "${fileName}": ${error.message}`);
    }
  };

  return (
    <>
      <Dialog
        open={open && phase === 'search'}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        scroll="body"
      >
        <form onSubmit={props.handleSubmit(handleSearch)}>
          <Container maxWidth="sm">
            <DialogTitle id="form-dialog-title">Link a Document</DialogTitle>
            <DialogContent classes={{ root: classes.dialogContent }}>
              <Grid container spacing={2} alignItems="center" justify="center">
                <Grid item md={12}>
                  <Field
                    name="store"
                    component={renderTextField}
                    inputLabel="Store *"
                    inputType="text"
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item md={12}>
                  <Field
                    name="doc_type"
                    component={renderSelectField}
                    inputLabel="Document Type *"
                    options={linkDocumentTypes}
                    onMenuOpen={onFetchLinkDocTypes}
                  />
                </Grid>
                <Grid item md={12}>
                  <Field
                    name="filename"
                    component={renderTextField}
                    inputLabel="Filename"
                    inputType="text"
                    fullWidth
                  />
                </Grid>
                <Grid item md={6}>
                  <Field
                    name="date_range_from"
                    component={renderDatePicker}
                    inputType="date"
                    inputLabel="Executed Date From"
                    fullWidth
                  />
                </Grid>
                <Grid item md={6}>
                  <Field
                    name="date_range_to"
                    component={renderDatePicker}
                    inputType="date"
                    inputLabel="Executed Date To"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Container>
          <DialogActions>
            <Button
              onClick={() => {
                reset();
                handleClose();
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSearching}
            >
              {isSearching ? <CircularProgress size={24} color="inherit" /> : 'Search'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={open && phase === 'results'}
        onClose={handleResultsClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.resultsDialogPaper }}
        scroll="paper"
      >
        <DialogTitle id="results-dialog-title">Search Results</DialogTitle>
        <DialogContent className={classes.resultsDialogContent}>
            {!!linkError && (
              <Typography color="error" style={{ marginBottom: 12 }}>
                {linkError}
              </Typography>
            )}
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>Store No</TableCell>
                <TableCell className={classes.sortableHeader}>
                  <TableSortLabel
                    active={orderBy === 'fileName'}
                    direction={orderBy === 'fileName' ? order : 'asc'}
                    onClick={() => handleRequestSort('fileName')}
                  >
                    File Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Document Type</TableCell>
                <TableCell className={classes.sortableHeader}>
                  <TableSortLabel
                    active={orderBy === 'executedDate'}
                    direction={orderBy === 'executedDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('executedDate')}
                  >
                    Executed Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Facility ID</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkDocumentsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: '40px 16px' }}>
                    <Typography variant="body1" color="textSecondary">
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortData(linkDocumentsData)
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                  <TableRow key={row.id} className={classes.tableRow}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(row.id)}
                        onChange={() => handleCheckboxChange(row.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{row.storeNumber}</TableCell>
                    <TableCell
                      className={classes.clickableCell}
                      onClick={() => handleOpenDocument(row.fileId || row.id, row.fileName)}
                      title="Click to open document"
                    >
                      {row.fileName}
                    </TableCell>
                    <TableCell>{row.documentType}</TableCell>
                    <TableCell>{row.executedDate}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.facilityID}</TableCell>
                    <TableCell>{row.address}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {linkDocumentsData.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={linkDocumentsData.length}
              page={page}
              onChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResultsClose}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            color="primary"
            variant="contained"
            disabled={selectedItems.length === 0}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

LinkDocuments.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  linkDocumentTypes: PropTypes.array,
  onFetchLinkDocTypes: PropTypes.func,
  onLinkCompleted: PropTypes.func,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  existingDocuments: PropTypes.array,
  reset: PropTypes.func,
  handleSubmit: PropTypes.func,
};

LinkDocuments.defaultProps = {
  linkDocumentTypes: [],
  onFetchLinkDocTypes: undefined,
  onLinkCompleted: () => {},
  projectId: '',
  categoryId: '',
  companyNumber: '',
  existingDocuments: [],
};

const validateLinkDocFields = (values) => {
  const errors = {};
  if (!values.store) {
    errors.store = 'Store is required';
  }
  if (!values.doc_type) {
    errors.doc_type = 'Document Type is required';
  }
  return errors;
};

const linkDocumentsForm = reduxForm({
  form: 'linkDocuments',
  enableReinitialize: true,
  validate: validateLinkDocFields,
  shouldError: () => true,
})(LinkDocuments);

const mapStateToProps = (state, ownProps) => ({
  linkDocumentTypes: state.document.linkDocumentTypes || [],
  initialValues: {
    store: ownProps.storeNumber || '',
  },
});

const mapDispatchToProps = (dispatch) => ({
  onFetchLinkDocTypes: () => dispatch(fetchLinkDocTypes()),
});

const reduxConnected = connect(mapStateToProps, mapDispatchToProps)(linkDocumentsForm);

export default withRouter(reduxConnected);